import { useState, type FormEvent } from "react";
import SectionHeading from "@/components/base/SectionHeading";
import Reveal from "@/components/base/Reveal";
import { useSiteContent } from "@/context/SiteContentContext";
import { buildWhatsAppLink } from "@/lib/cms";
import { supabase } from "@/lib/supabase";

const projectTypes = [
  "Página web",
  "Sistema empresarial",
  "Aplicación",
  "Catálogo",
  "Comercio electrónico",
  "Automatización",
  "Aplicación móvil",
  "Otro",
];

const inputClass =
  "w-full rounded-lg border border-background-200 bg-background-50 px-4 py-3 text-sm text-foreground-900 placeholder:text-foreground-400 outline-none transition-colors focus:border-primary-400 focus:ring-2 focus:ring-primary-100";

export default function Contact() {
  const { settings, sections } = useSiteContent();
  const content = sections.contacto || {};

  const [status, setStatus] = useState<"idle" | "loading" | "success" | "error">("idle");
  const [formError, setFormError] = useState("");

  const whatsappNumber = String(settings.whatsapp_number || "");
  const whatsappMessage = String(settings.whatsapp_message || "");

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;

    const honeypot = form.querySelector<HTMLInputElement>('input[name="company_alt"]');
    if (honeypot && honeypot.value.trim() !== "") {
      setStatus("success");
      setFormError("");
      return;
    }

    setStatus("loading");
    setFormError("");

    const formData = new FormData(form);
    const payload = {
      nombre: String(formData.get("nombre") || ""),
      empresa: formData.get("empresa") ? String(formData.get("empresa")) : null,
      email: formData.get("email") ? String(formData.get("email")) : null,
      whatsapp: formData.get("whatsapp") ? String(formData.get("whatsapp")) : null,
      tipo_proyecto: formData.get("tipo_proyecto") ? String(formData.get("tipo_proyecto")) : null,
      presupuesto: formData.get("presupuesto") ? String(formData.get("presupuesto")) : null,
      mensaje: formData.get("mensaje") ? String(formData.get("mensaje")) : null,
    };

    const { error } = await supabase.from("contact_requests").insert(payload);

    if (error) {
      setStatus("error");
      setFormError("Hubo un problema al enviar el formulario. Inténtalo de nuevo.");
      return;
    }

    setStatus("success");
    setFormError("");
    form.reset();
  };

  return (
    <section id="contacto" className="relative py-20 md:py-28 overflow-hidden">
      <div className="absolute inset-0 digital-grid opacity-60" />
      <div className="absolute -top-20 right-0 w-96 h-96 rounded-full bg-primary-500/10 blur-3xl" />

      <div className="relative max-w-7xl mx-auto px-4 md:px-8 lg:px-12">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-start">
          <Reveal variant="left">
            <SectionHeading
              align="left"
              eyebrow="Contacto"
              title={String(content.title || "¿Tienes una idea?")}
            />
            <p className="mt-5 text-base md:text-lg text-foreground-600 leading-relaxed">
              {String(
                content.description ||
                  "Cuéntanos qué necesitas y construyamos juntos la solución digital para tu negocio."
              )}
            </p>

            <div className="mt-8 flex flex-col sm:flex-row lg:flex-col xl:flex-row gap-3">
              <a
                href={buildWhatsAppLink(whatsappNumber, whatsappMessage)}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center gap-2 rounded-full bg-[#25D366] hover:bg-[#1fb855] text-background-50 px-6 py-3.5 text-sm font-semibold transition-all whitespace-nowrap"
              >
                <i className="fa-brands fa-whatsapp text-lg" />
                {String(content.btn_text || "Hablar por WhatsApp")}
              </a>
              <a
                href="#proyectos"
                className="inline-flex items-center justify-center gap-2 rounded-full border border-primary-300 text-primary-700 hover:bg-primary-50 px-6 py-3.5 text-sm font-semibold transition-all whitespace-nowrap"
              >
                Ver proyectos
              </a>
            </div>

            <div className="mt-10 flex items-center gap-4 p-5 rounded-2xl border border-background-200 bg-background-50">
              <span className="w-12 h-12 flex items-center justify-center rounded-xl bg-primary-50 text-primary-600">
                <i className="ri-time-line text-2xl" />
              </span>
              <div>
                <p className="font-semibold text-foreground-950">Respuesta rápida</p>
                <p className="text-sm text-foreground-500">
                  Te respondemos a la brevedad para arrancar tu proyecto.
                </p>
              </div>
            </div>
          </Reveal>

          <Reveal variant="right">
            <form
              onSubmit={handleSubmit}
              data-readdy-form
              className="rounded-3xl border border-background-200 bg-background-50 p-6 md:p-8 shadow-[0_20px_50px_rgba(30,60,120,0.08)]"
            >
              <h3 className="text-xl md:text-2xl font-semibold text-foreground-950 mb-6">
                Solicitar proyecto
              </h3>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="sm:col-span-1">
                  <label htmlFor="nombre" className="block text-sm font-medium text-foreground-700 mb-1.5">
                    Nombre *
                  </label>
                  <input
                    id="nombre"
                    name="nombre"
                    type="text"
                    required
                    autoComplete="name"
                    placeholder="Tu nombre"
                    className={inputClass}
                  />
                </div>

                <div className="sm:col-span-1">
                  <label htmlFor="empresa" className="block text-sm font-medium text-foreground-700 mb-1.5">
                    Empresa
                  </label>
                  <input
                    id="empresa"
                    name="empresa"
                    type="text"
                    autoComplete="organization"
                    placeholder="Nombre de tu empresa"
                    className={inputClass}
                  />
                </div>

                <div className="sm:col-span-1">
                  <label htmlFor="email" className="block text-sm font-medium text-foreground-700 mb-1.5">
                    Correo electrónico *
                  </label>
                  <input
                    id="email"
                    name="email"
                    type="email"
                    required
                    autoComplete="email"
                    placeholder="correo@ejemplo.com"
                    className={inputClass}
                  />
                </div>

                <div className="sm:col-span-1">
                  <label htmlFor="whatsapp" className="block text-sm font-medium text-foreground-700 mb-1.5">
                    WhatsApp
                  </label>
                  <input
                    id="whatsapp"
                    name="whatsapp"
                    type="tel"
                    autoComplete="tel"
                    placeholder="Tu número de WhatsApp"
                    className={inputClass}
                  />
                </div>

                <div className="sm:col-span-1">
                  <label htmlFor="tipo_proyecto" className="block text-sm font-medium text-foreground-700 mb-1.5">
                    Tipo de proyecto *
                  </label>
                  <select
                    id="tipo_proyecto"
                    name="tipo_proyecto"
                    required
                    className={inputClass}
                    defaultValue=""
                  >
                    <option value="" disabled>
                      Selecciona un tipo
                    </option>
                    {projectTypes.map((type) => (
                      <option key={type} value={type}>
                        {type}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="sm:col-span-1">
                  <label htmlFor="presupuesto" className="block text-sm font-medium text-foreground-700 mb-1.5">
                    Presupuesto aproximado
                  </label>
                  <input
                    id="presupuesto"
                    name="presupuesto"
                    type="text"
                    placeholder="Rango estimado (opcional)"
                    className={inputClass}
                  />
                </div>

                <div className="sm:col-span-2">
                  <label htmlFor="mensaje" className="block text-sm font-medium text-foreground-700 mb-1.5">
                    Mensaje *
                  </label>
                  <textarea
                    id="mensaje"
                    name="mensaje"
                    required
                    maxLength={500}
                    rows={4}
                    placeholder="Cuéntanos sobre tu proyecto..."
                    className={`${inputClass} resize-none`}
                  />
                  <p className="text-xs text-foreground-400 mt-1 text-right">
                    Máx. 500 caracteres
                  </p>
                </div>
              </div>

              <input
                type="text"
                name="company_alt"
                tabIndex={-1}
                autoComplete="off"
                aria-hidden="true"
                readOnly
                className="hp-field"
              />

              {status === "error" && (
                <div className="mt-4 rounded-lg bg-red-50 border border-red-200 text-red-700 text-sm px-4 py-3">
                  {formError}
                </div>
              )}

              {status === "success" && (
                <div className="mt-4 rounded-lg bg-green-50 border border-green-200 text-green-700 text-sm px-4 py-3">
                  ¡Gracias! Recibimos tu solicitud. Te contactaremos pronto.
                </div>
              )}

              <button
                type="submit"
                disabled={status === "loading"}
                className="mt-6 w-full inline-flex items-center justify-center gap-2 rounded-full bg-primary-500 hover:bg-primary-600 disabled:opacity-60 disabled:cursor-not-allowed text-background-50 px-6 py-3.5 text-sm font-semibold transition-all whitespace-nowrap shadow-[0_0_24px_oklch(var(--primary-500)/0.35)]"
              >
                {status === "loading" ? (
                  <>
                    <i className="ri-loader-4-line animate-spin" />
                    Enviando...
                  </>
                ) : (
                  <>
                    Solicitar proyecto
                    <i className="ri-send-plane-line" />
                  </>
                )}
              </button>
            </form>
          </Reveal>
        </div>
      </div>
    </section>
  );
}